(() => {
  const initSlider = (root) => {
    const track = root.querySelector('[data-sc-slider-track]');
    const dots = [...root.querySelectorAll('[data-sc-slider-dot]')];
    if (!track || !dots.length || root.dataset.scReady) return;
    root.dataset.scReady = 'true';
    const slides = [...track.children];
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const mark = (i) => dots.forEach((dot, n) => dot.setAttribute('aria-current', String(n === i)));
    let current = 0;
    let go;

    if (root.dataset.scTransition && root.dataset.scTransition !== 'slide' && !reduced) {
      // Slides stacked in one place: the next one wipes or fades in over the last.
      root.classList.add('sc-hero--fx');
      slides.forEach((slide, n) => {
        slide.classList.toggle('is-active', n === 0);
        slide.inert = n !== 0;
      });
      let settle;
      go = (i, dir = i > current ? 1 : -1) => {
        if (i === current) return;
        const from = slides[current];
        const to = slides[i];
        root.classList.remove('sc-hero--intro');
        root.dataset.scDir = dir > 0 ? 'next' : 'prev';
        slides.forEach((slide) => slide.classList.remove('is-leaving', 'is-entering'));
        from.classList.replace('is-active', 'is-leaving');
        from.inert = true;
        to.classList.add('is-active');
        to.inert = false;
        void to.offsetWidth;
        to.classList.add('is-entering');
        current = i;
        mark(i);
        clearTimeout(settle);
        settle = setTimeout(() => {
          from.classList.remove('is-leaving');
          to.classList.remove('is-entering');
        }, 1800);
      };
      let x0 = null;
      track.addEventListener('touchstart', (event) => (x0 = event.touches[0].clientX), { passive: true });
      track.addEventListener('touchend', (event) => {
        if (x0 === null) return;
        const dx = event.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) < 50) return;
        const step = dx < 0 ? 1 : -1;
        go((current + step + slides.length) % slides.length, step);
      });
    } else {
      go = (i) => track.scrollTo({ left: i * track.clientWidth });
      let ticking = false;
      track.addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          current = Math.round(track.scrollLeft / track.clientWidth);
          mark(current);
          ticking = false;
        });
      });
    }
    root.scGo = go;
    dots.forEach((dot, i) => dot.addEventListener('click', () => go(i)));

    // Auto-advance: the current dot fills over the set time, then the next slide comes in.
    // Paused on hover and keyboard focus; off for reduced motion and in the theme editor.
    const seconds = Number(root.dataset.scAutoplay);
    if (seconds && !reduced && !window.Shopify?.designMode) {
      root.style.setProperty('--sc-autoplay', `${seconds}s`);
      root.classList.add('sc-hero--auto');
      const pause = (on) => root.classList.toggle('is-paused', on);
      root.addEventListener('mouseenter', () => pause(true));
      root.addEventListener('mouseleave', () => pause(false));
      root.addEventListener('focusin', () => pause(true));
      root.addEventListener('focusout', () => pause(false));
      dots.forEach((dot) =>
        dot.addEventListener('animationend', () => {
          if (dot.getAttribute('aria-current') === 'true') go((current + 1) % slides.length, 1);
        })
      );
    }
  };

  const initGallery = (root) => {
    if (root.dataset.scReady) return;
    root.dataset.scReady = 'true';
    const images = [...root.querySelectorAll('[data-sc-gallery-image]')];
    root.querySelectorAll('[data-sc-gallery-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const target = thumb.dataset.scGalleryThumb;
        images.forEach((img) => img.classList.toggle('is-active', img.dataset.scGalleryImage === target));
        root.querySelectorAll('[data-sc-gallery-thumb]').forEach((t) => t.setAttribute('aria-current', String(t === thumb)));
      });
    });
  };

  // Collection and search: products per row on desktop, remembered for this visit (a new visit opens at 4).
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-sc-cols]');
    if (!button) return;
    document.documentElement.dataset.scCols = button.dataset.scCols;
    try {
      sessionStorage.setItem('scCols', button.dataset.scCols);
    } catch (e) {}
  });

  // Announcement bar closed by the visitor: stays closed for this visit.
  document.addEventListener('click', (event) => {
    if (!event.target.closest('[data-sc-announce-close]')) return;
    document.documentElement.classList.add('sc-announce-closed');
    try {
      sessionStorage.setItem('scAnnounceClosed', '1');
    } catch (e) {}
  });

  // Page header: a long collection description opens in full.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-sc-more]');
    if (!button) return;
    const text = document.getElementById(button.getAttribute('aria-controls'));
    const open = text.classList.toggle('is-open');
    button.setAttribute('aria-expanded', open);
    [button.textContent, button.dataset.scLess] = [button.dataset.scLess, button.textContent];
  });

  // Footer link columns fold away on phones.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-sc-fold]');
    if (!button) return;
    button.setAttribute('aria-expanded', button.getAttribute('aria-expanded') !== 'true');
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-sc-qty]');
    if (!button) return;
    const input = button.parentElement.querySelector('input');
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + Number(button.dataset.scQty));
  });

  // Scroll-linked movement: --sc-p runs 0 to 1 as an element crosses the viewport;
  // --sc-y is its top as a share of the screen height (1 at the bottom edge, 0 at the top).
  const initParallax = () => {
    if (!document.body.classList.contains('sc-motion')) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const visible = new Set();
    const movers = new Map();
    let queued = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        const els = movers.get(e.target) || [e.target];
        els.forEach((el) => (e.isIntersecting ? visible.add(el) : visible.delete(el)));
      });
      queue();
    });
    // An element slid off to the side is clipped out of view, so it is watched through its
    // parent; otherwise it would stop updating while out of view and never come back.
    const watch = () =>
      document.querySelectorAll('[data-sc-parallax]').forEach((el) => {
        if (!el.dataset.scParallax.startsWith('in-')) return observer.observe(el);
        const parent = el.parentElement;
        if (!movers.has(parent)) movers.set(parent, []);
        if (!movers.get(parent).includes(el)) movers.get(parent).push(el);
        observer.unobserve(parent);
        observer.observe(parent);
      });
    const update = () => {
      queued = false;
      const vh = window.innerHeight;
      visible.forEach((el) => {
        const r = el.getBoundingClientRect();
        const p = Math.min(1, Math.max(0, (vh - r.top) / (vh + r.height)));
        el.style.setProperty('--sc-p', p.toFixed(3));
        el.style.setProperty('--sc-y', (r.top / vh).toFixed(3));
      });
    };
    const queue = () => {
      if (!queued) {
        queued = true;
        requestAnimationFrame(update);
      }
    };
    watch();
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue);
    document.addEventListener('shopify:section:load', watch);
    queue();
  };
  initParallax();

  // The header laid over the hero sits below the announcement bar and the affiliate line
  // (snippets/sc-affiliate-bar.liquid), whatever their height.
  const announce = document.querySelector('.announcement-bar-section');
  const affiliate = document.querySelector('.sc-affiliate-bar');
  const setAnnounce = () =>
    document.documentElement.style.setProperty(
      '--sc-announce-h',
      `${(announce ? announce.offsetHeight : 0) + (affiliate ? affiliate.offsetHeight : 0)}px`,
    );
  setAnnounce();
  const sizes = new ResizeObserver(setAnnounce);
  [announce, affiliate].forEach((el) => el && sizes.observe(el));

  // Desktop menu: dropdowns open on hover where there is a mouse; a click on the label then
  // follows its link. Touch screens keep Dawn's tap to open.
  const pointer = { x: -1, y: -1 };
  document.addEventListener('pointermove', (event) => {
    pointer.x = event.clientX;
    pointer.y = event.clientY;
  }, { passive: true });
  const initMenuHover = (scope) => {
    if (!window.matchMedia('(hover: hover)').matches) return;
    scope.querySelectorAll('.header__inline-menu header-menu').forEach((menu) => {
      if (menu.dataset.scReady) return;
      menu.dataset.scReady = 'true';
      const details = menu.querySelector('details');
      const summary = details.querySelector('summary');
      const panel = details.querySelector('.mega-menu__content, .header__submenu');
      let timer;
      // While the panel unrolls, its clipped part does not count as hovered: treat the pointer
      // as inside anywhere in the panel's full box, so it does not close and reopen.
      const overPanel = () => {
        if (!panel || !details.open) return false;
        const box = panel.getBoundingClientRect();
        return pointer.x >= box.left && pointer.x <= box.right && pointer.y >= box.top && pointer.y <= box.bottom;
      };
      // Leaving fades the panel out (.is-closing in sloane.css) before Dawn closes it.
      const shut = (item) => {
        clearTimeout(item.scFade);
        item.querySelector('details').classList.remove('is-closing');
        item.close();
      };
      // Chrome skips style for a closed <details>, so the opening animations would play once
      // only: rewind them on every open.
      const replay = () => {
        if (!details.open) return;
        details.getAnimations({ subtree: true }).forEach((animation) => {
          animation.currentTime = 0;
          animation.play();
        });
      };
      details.addEventListener('toggle', replay);
      const fadeOut = () => {
        if (!details.open || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return shut(menu);
        details.classList.add('is-closing');
        menu.scFade = setTimeout(() => shut(menu), 220);
      };
      menu.addEventListener('mouseenter', () => {
        clearTimeout(timer);
        document.querySelectorAll('.header__inline-menu details[open]').forEach((other) => {
          if (other !== details) shut(other.closest('header-menu'));
        });
        if (details.classList.contains('is-closing')) {
          // Back in before it finished fading: stay open, shown in full.
          clearTimeout(menu.scFade);
          details.classList.remove('is-closing');
          details.getAnimations({ subtree: true }).forEach((animation) => animation.finish());
        } else if (!details.open) {
          details.open = true;
          summary.setAttribute('aria-expanded', 'true');
          replay();
        }
      });
      const leave = () => {
        timer = setTimeout(() => (overPanel() ? leave() : fadeOut()), 250);
      };
      menu.addEventListener('mouseleave', () => {
        clearTimeout(timer);
        leave();
      });
      summary.addEventListener('click', (event) => {
        if (!details.open) return;
        event.preventDefault();
        if (summary.dataset.scHref) window.location.href = summary.dataset.scHref;
        requestAnimationFrame(() => summary.setAttribute('aria-expanded', 'true'));
      });
    });
  };

  // Tabs in a mega menu: hover or click a tab to show its products.
  const initTabs = (root) => {
    if (root.dataset.scReady) return;
    root.dataset.scReady = 'true';
    const tabs = [...root.querySelectorAll('[data-sc-tab]')];
    const show = (tab) => {
      tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', String(on));
        t.tabIndex = on ? 0 : -1;
        document.getElementById(t.getAttribute('aria-controls')).hidden = !on;
      });
    };
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => show(tab));
      tab.addEventListener('mouseenter', () => show(tab));
      tab.addEventListener('keydown', (event) => {
        const step = { ArrowRight: 1, ArrowLeft: -1 }[event.key];
        if (!step) return;
        const next = tabs[(i + step + tabs.length) % tabs.length];
        show(next);
        next.focus();
      });
    });
  };

  // Background videos stand still for visitors who ask for less motion.
  const initVideos = (scope) => {
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    scope.querySelectorAll('.sc-banner video').forEach((video) => {
      video.removeAttribute('autoplay');
      video.pause();
    });
  };

  // Scrolling banner photos fade from one to the next; held still while paused.
  const initRotate = (banner) => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const photos = [...banner.querySelectorAll('.sc-marquee__photo')];
    banner.scShow = (photo) => photos.forEach((p) => p.classList.toggle('is-active', p === photo));
    setInterval(() => {
      if (banner.classList.contains('is-paused') || document.hidden) return;
      const i = photos.findIndex((p) => p.classList.contains('is-active'));
      banner.scShow(photos[(i + 1) % photos.length]);
    }, Number(banner.dataset.scRotate) * 1000);
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-sc-slider]').forEach(initSlider);
    scope.querySelectorAll('[data-sc-gallery]').forEach(initGallery);
    scope.querySelectorAll('[data-sc-tabs]').forEach(initTabs);
    scope.querySelectorAll('[data-sc-rotate]').forEach(initRotate);
    initMenuHover(scope);
    initVideos(scope);
  };

  // Header over the home hero: the logo, menu words and icons turn peach where the photo
  // behind them is dark, letter by letter. The hero behind the header is redrawn into a
  // canvas; each pixel takes whichever of ink or peach contrasts more with it. The logo
  // uses that as the mask of its peach copy; words and icons are painted with it.
  const initLogoAdapt = () => {
    const logo = document.querySelector('[data-sc-logo-adapt]');
    const hero = document.querySelector('.sc-hero');
    if (!logo || !hero) return;
    const alt = logo.querySelector('.sc-logo__alt');
    const header = logo.closest('.section-header') || logo.parentElement;
    const bar = logo.closest('header') || header;
    const words = [...bar.querySelectorAll('.header__menu-item > span')].filter((el) => !el.closest('.mega-menu__content, .header__submenu'));
    const icons = [...bar.querySelectorAll('.header__icons svg, header-drawer summary svg')];
    // Each icon's own shape, for cutting the colour picture to it.
    const shapes = new Map(
      icons.map((svg) => {
        const copy = svg.cloneNode(true);
        copy.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
        const markup = new XMLSerializer().serializeToString(copy).replaceAll('currentColor', '#000');
        return [svg, `url("data:image/svg+xml,${encodeURIComponent(markup)}")`];
      })
    );
    const root = getComputedStyle(document.documentElement);
    const rgb = (hex) => hex.trim().replace('#', '').match(/../g).map((x) => parseInt(x, 16));
    const ink = rgb(root.getPropertyValue('--sc-ink') || '#1e1e1e');
    const peach = rgb(root.getPropertyValue('--sc-peach') || '#fab491');
    const sample = document.createElement('canvas');
    const ctx = sample.getContext('2d', { willReadFrequently: true });
    const piece = document.createElement('canvas');
    const pieceCtx = piece.getContext('2d');
    let field = null;
    let fieldBox = null;
    // Light (relative luminance) of each sRGB channel value.
    const linear = Array.from({ length: 256 }, (_, v) => {
      const c = v / 255;
      return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
    });
    // Ink #1e1e1e and peach #fab491 contrast equally at a luminance of about 0.144;
    // the change is softened either side so a letter can blend from one to the other.
    const darkFrom = 0.18;
    const darkTo = 0.11;
    const solid = (el) => {
      const colour = getComputedStyle(el).backgroundColor;
      const alpha = colour.match(/[\d.]+/g)?.[3];
      return colour !== 'transparent' && (alpha === undefined || Number(alpha) === 1) ? colour : null;
    };
    const offset = (value, room) => (value.endsWith('%') ? (room * parseFloat(value)) / 100 : parseFloat(value));
    // The part of a slide its wipe has uncovered so far (clip-path: inset(...)).
    const uncovered = (el, r) => {
      const inset = getComputedStyle(el).clipPath.match(/^inset\((.*)\)$/);
      if (!inset) return r;
      const [t, rt = t, b = t, l = rt] = inset[1].split(' ');
      const top = offset(t, r.height);
      const left = offset(l, r.width);
      return new DOMRect(r.left + left, r.top + top, r.width - left - offset(rt, r.width), r.height - top - offset(b, r.height));
    };
    const drawImage = (img) => {
      if (!img.complete || !img.naturalWidth || !img.getClientRects().length) return;
      const r = img.getBoundingClientRect();
      const style = getComputedStyle(img);
      const fit = style.objectFit === 'contain' ? Math.min : Math.max;
      const k = fit(r.width / img.naturalWidth, r.height / img.naturalHeight);
      const w = img.naturalWidth * k;
      const h = img.naturalHeight * k;
      const [px, py = '50%'] = style.objectPosition.split(' ');
      ctx.save();
      ctx.beginPath();
      ctx.rect(r.left, r.top, r.width, r.height);
      ctx.clip();
      ctx.drawImage(img, r.left + offset(px, r.width - w), r.top + offset(py, r.height - h), w, h);
      ctx.restore();
    };
    // How far each pixel behind the header leans to peach, 0 to 1.
    const sampleField = () => {
      const box = bar.getBoundingClientRect();
      const w = Math.ceil(box.width);
      const h = Math.ceil(box.height);
      if (!w || !h) return false;
      sample.width = w;
      sample.height = h;
      ctx.setTransform(1, 0, 0, 1, -box.left, -box.top);
      ctx.filter = 'blur(1.5px)';
      // A solid header (scrolled down, menu open) stands in front of the hero.
      let behind = null;
      for (let el = bar; el && el !== document.body && !behind; el = el.parentElement) behind = solid(el);
      ctx.fillStyle = behind || solid(hero) || solid(document.body) || '#fff';
      ctx.fillRect(box.left, box.top, box.width, box.height);
      if (!behind) {
        const fx = hero.classList.contains('sc-hero--fx');
        [...hero.querySelectorAll('[data-sc-slide]')]
          .filter((slide) => !fx || slide.matches('.is-active, .is-leaving'))
          .sort((a, b) => a.classList.contains('is-active') - b.classList.contains('is-active'))
          .forEach((slide) => {
            const r = uncovered(slide, slide.getBoundingClientRect());
            if (r.right <= box.left || r.left >= box.right || r.bottom <= box.top || r.top >= box.bottom) return;
            ctx.save();
            ctx.globalAlpha = Number(getComputedStyle(slide).opacity);
            ctx.beginPath();
            ctx.rect(r.left, r.top, r.width, r.height);
            ctx.clip();
            ctx.fillStyle = solid(slide) || 'transparent';
            ctx.fillRect(r.left, r.top, r.width, r.height);
            slide.querySelectorAll('img').forEach(drawImage);
            ctx.restore();
          });
      }
      let d;
      try {
        d = ctx.getImageData(0, 0, w, h).data;
      } catch {
        return false;
      }
      field = new Float32Array(w * h);
      for (let p = 0; p < field.length; p++) {
        const i = p * 4;
        const light = 0.2126 * linear[d[i]] + 0.7152 * linear[d[i + 1]] + 0.0722 * linear[d[i + 2]];
        const t = Math.min(1, Math.max(0, (darkFrom - light) / (darkFrom - darkTo)));
        field[p] = t * t * (3 - 2 * t);
      }
      fieldBox = { left: box.left, top: box.top, w, h };
      return true;
    };
    // The field under one element as an image: a mask (alpha) or ink-to-peach colour.
    // Null when nothing under it leans to peach, so the element keeps its own colour.
    const imageFor = (el, asMask) => {
      const r = el.getBoundingClientRect();
      const w = Math.round(r.width);
      const h = Math.round(r.height);
      if (!w || !h) return null;
      const x0 = Math.round(r.left - fieldBox.left);
      const y0 = Math.round(r.top - fieldBox.top);
      piece.width = w;
      piece.height = h;
      const out = pieceCtx.createImageData(w, h);
      let any = false;
      let sum = 0;
      for (let y = 0; y < h; y++) {
        const fy = Math.min(fieldBox.h - 1, Math.max(0, y0 + y));
        for (let x = 0; x < w; x++) {
          const fx = Math.min(fieldBox.w - 1, Math.max(0, x0 + x));
          const t = field[fy * fieldBox.w + fx];
          if (t > 0.01) any = true;
          sum += t;
          const i = (y * w + x) * 4;
          if (asMask) {
            out.data[i + 3] = t * 255;
          } else {
            for (let c = 0; c < 3; c++) out.data[i + c] = ink[c] + (peach[c] - ink[c]) * t;
            out.data[i + 3] = 255;
          }
        }
      }
      if (!any) return null;
      pieceCtx.putImageData(out, 0, 0);
      return { url: `url(${piece.toDataURL()})`, peach: sum / (w * h) > 0.5 };
    };
    const plain = (el) => {
      if (!('scTinted' in el.dataset)) return;
      el.style.removeProperty('background');
      el.style.removeProperty('-webkit-background-clip');
      el.style.removeProperty('background-clip');
      el.style.removeProperty('color');
      el.style.removeProperty('mask');
      el.style.removeProperty('text-decoration-color');
      delete el.dataset.scTinted;
    };
    const paint = () => {
      if (!sampleField()) return;
      const logoMask = imageFor(logo, true);
      if (logoMask) alt.style.setProperty('--sc-logo-mask', logoMask.url);
      else alt.style.removeProperty('--sc-logo-mask');
      words.forEach((el) => {
        const image = imageFor(el, false);
        if (!image) return plain(el);
        el.dataset.scTinted = '';
        el.style.background = `${image.url} 0 0 / 100% 100%`;
        el.style.setProperty('-webkit-background-clip', 'text');
        el.style.backgroundClip = 'text';
        el.style.color = 'transparent';
        el.style.textDecorationColor = `rgb(${(image.peach ? peach : ink).join(' ')})`;
      });
      icons.forEach((el) => {
        const image = imageFor(el, false);
        if (!image) return plain(el);
        el.dataset.scTinted = '';
        el.style.background = `${image.url} 0 0 / 100% 100%`;
        el.style.mask = `${shapes.get(el)} 0 0 / 100% 100% no-repeat`;
        el.style.color = 'transparent';
      });
    };
    // Repaint every frame for a while after anything that moves the picture behind the logo.
    let until = 0;
    let frame = 0;
    const loop = (now) => {
      paint();
      frame = now < until ? requestAnimationFrame(loop) : 0;
    };
    const burst = (ms) => {
      until = Math.max(until, performance.now() + ms);
      if (!frame) frame = requestAnimationFrame(loop);
    };
    const watch = { attributes: true, subtree: true, attributeFilter: ['class', 'open'] };
    new MutationObserver(() => burst(2000)).observe(hero, watch);
    new MutationObserver(() => burst(500)).observe(header, watch);
    hero.querySelectorAll('img').forEach((img) => img.addEventListener('load', () => burst(100)));
    hero.querySelector('[data-sc-slider-track]')?.addEventListener('scroll', () => burst(300), { passive: true });
    window.addEventListener('scroll', () => burst(100), { passive: true });
    window.addEventListener('resize', () => burst(300));
    burst(2500);
  };

  // Affiliate link for an empty cart: hash the cart's codes and look them up
  // in the map printed by snippets/sc-affiliate-bar.liquid.
  const initAffiliateBar = async () => {
    const bar = document.querySelector('[data-sc-affiliates]');
    if (!bar || !window.crypto?.subtle) return;
    const affiliates = JSON.parse(bar.dataset.scAffiliates);
    if (!Object.keys(affiliates).length) return;
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((res) => res.json()).catch(() => null);
    for (const { code } of cart?.discount_codes || []) {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(code.toLowerCase()));
      const hash = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
      const affiliate = affiliates[hash];
      if (!affiliate) continue;
      const link = bar.querySelector('a');
      link.href = affiliate.url;
      link.textContent = bar.dataset.scText.replace('[name]', affiliate.name);
      bar.hidden = false;
      return;
    }
  };

  // Affiliate code on their picks page: copy it, and say so when the cart already has it.
  const initAffiliateCode = async () => {
    const box = document.querySelector('[data-sc-code]');
    if (!box) return;
    const code = box.dataset.scCode;
    const button = box.querySelector('.sc-affiliate-code__copy');
    button.addEventListener('click', async () => {
      try {
        await navigator.clipboard.writeText(code);
        const label = button.textContent;
        button.textContent = button.dataset.scCopied;
        setTimeout(() => (button.textContent = label), 2000);
      } catch {
        window.getSelection().selectAllChildren(box.querySelector('strong'));
      }
    });
    const cart = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((res) => res.json()).catch(() => null);
    if ((cart?.discount_codes || []).some((d) => d.applicable && d.code.toLowerCase() === code.toLowerCase())) {
      box.querySelector('.sc-affiliate-code__applied').hidden = false;
    }
  };

  init();
  initLogoAdapt();
  initAffiliateBar();
  initAffiliateCode();
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  // Theme editor: show the slide the client has selected.
  document.addEventListener('shopify:block:select', (event) => {
    const slide = event.target.closest('[data-sc-slide]');
    const slider = slide?.closest('[data-sc-slider]');
    if (slider?.scGo) slider.scGo(Number(slide.dataset.scSlide));
    // Open the menu item's dropdown so its panel can be seen while editing.
    const item = event.target.querySelector(':scope > header-menu');
    if (item) {
      item.querySelector('details').open = true;
    }
    // Hold scrolling rows still so the selected item stays in view.
    event.target.closest('.sc-marquee, .sc-strip')?.classList.add('is-paused');
    if (event.target.matches('.sc-marquee__photo')) event.target.parentElement.scShow?.(event.target);
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    event.target.closest('.sc-marquee, .sc-strip')?.classList.remove('is-paused');
    event.target.querySelector(':scope > header-menu')?.close();
  });
})();
